import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RecurrenceInboxEntry } from '@this+that/models';
import log from 'loglevel';

import { useMoveRecurrenceInboxEntryToTaskGroupService } from '../../services/recurrenceInboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

const syncActiveTaskGroupSlice = (
  updatedEntry: RecurrenceInboxEntry,
  activeTaskGroupId: string | null,
  targetTaskGroupId: string,
  updateRecurrenceInboxEntry: (entry: RecurrenceInboxEntry) => void,
  removeRecurrenceInboxEntry: (id: string) => void,
) => {
  if (!activeTaskGroupId) {
    return;
  }

  if (activeTaskGroupId === targetTaskGroupId) {
    updateRecurrenceInboxEntry(updatedEntry);
  } else {
    removeRecurrenceInboxEntry(updatedEntry.id);
  }
};

export const useMoveRecurrenceInboxEntryToTaskGroup = () => {
  const { t } = useTranslation();
  const { errorMessage } = useCustomToast();

  const { moveRecurrenceInboxEntryToTaskGroup: moveService } =
    useMoveRecurrenceInboxEntryToTaskGroupService();

  const activeTaskGroupId = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup.taskGroupId,
  );

  const { updateRecurrenceInboxEntry, removeRecurrenceInboxEntry } =
    useInboxEntriesStore(
      (state) => state.taskManager.activeTaskGroup.recurrenceEntries,
    );

  const [isMovingToTaskGroup, setIsMovingToTaskGroup] = useState(false);

  const moveToTaskGroup = useCallback(
    async (
      taskGroupId: string,
      id: string,
      newTaskGroupId: string,
    ): Promise<boolean> => {
      setIsMovingToTaskGroup(true);
      try {
        const updatedEntry = await moveService(taskGroupId, id, newTaskGroupId);
        if (!updatedEntry) {
          errorMessage(t('doBox.recurrences.moveError'));
          return false;
        }

        syncActiveTaskGroupSlice(
          updatedEntry,
          activeTaskGroupId,
          newTaskGroupId,
          updateRecurrenceInboxEntry,
          removeRecurrenceInboxEntry,
        );

        return true;
      } catch (error) {
        log.error('Error moving recurrence entry to task group:', error);
        errorMessage(t('doBox.recurrences.moveError'));
        return false;
      } finally {
        setIsMovingToTaskGroup(false);
      }
    },
    [
      moveService,
      activeTaskGroupId,
      updateRecurrenceInboxEntry,
      removeRecurrenceInboxEntry,
      errorMessage,
      t,
    ],
  );

  return { moveToTaskGroup, isMovingToTaskGroup };
};
